import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { adminGetPost } from "@/lib/blog-admin.functions";

import { Prose } from "@/components/site/Prose";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import {
  ArticleHeader,
  ArticleTakeaways,
  ArticleOutline,
  headingComponents,
} from "@/components/blog/ArticleFurniture";
import { QuickDscrCheck } from "@/components/blog/QuickDscrCheck";
import { QuickQuoteWidget } from "@/components/site/QuickQuoteWidget";
import { AskSamCard } from "@/components/blog/AskSamCard";
import { ArticleEnding } from "@/components/blog/ArticleEnding";
import {
  extractOutline,
  extractQuickAnswer,
  extractTakeaways,
  cleanArticleMarkdown,
  extractExpertInsight,
  splitArticleForBanners,
  blogProductKey,
} from "@/lib/blog/article-outline";
import { BLOG_CATEGORIES, formatDate } from "@/lib/blog";
import { lazy, Suspense } from "react";

// Ask SAM chat so the in-article Ask SAM card works in previews, same as live.
const ChatWidget = lazy(() =>
  import("@/components/site/ChatWidget").then((m) => ({ default: m.ChatWidget })),
);

export const Route = createFileRoute("/admin/posts/$id_/preview")({
  component: PreviewPost,
  head: () => ({
    meta: [
      { title: "Preview post · Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function PreviewPost() {
  const { id } = Route.useParams();
  const fetchPost = useServerFn(adminGetPost);
  const q = useQuery({
    queryKey: ["admin-post", id],
    queryFn: () => fetchPost({ data: { id } }),
  });

  if (q.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container-editorial py-16">
          <p className="text-sm text-muted-foreground">Loading preview…</p>
        </div>
      </div>
    );
  }
  if (q.error || !q.data) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container-editorial py-16">
          <p className="text-sm text-destructive">Couldn't load post.</p>
          <Link to="/admin/posts/$id" params={{ id }} className="text-primary">
            ← Back to editor
          </Link>
        </div>
      </div>
    );
  }

  const post = q.data as any;
  const cover = post.cover_url || post.hero_image_url || null;
  const published = post.published_at ?? post.updated_at;
  const author = post.author_name || "Simply Approved Mortgages";
  const rawBody: string = post.body_md ?? "";
  const body = cleanArticleMarkdown(rawBody, post.title);
  const expertInsight = extractExpertInsight(rawBody);
  const path = `/blog/${post.slug}`;
  const categorySlug = post.category ?? null;
  const categoryName =
    BLOG_CATEGORIES.find(
      (c) => c.slug === categorySlug || c.name === categorySlug,
    )?.name ?? categorySlug ?? "Article";
  const outline = extractOutline(body);
  const takeaways = extractTakeaways(rawBody);
  const quickAnswer = extractQuickAnswer(rawBody) || post.excerpt?.trim() || "";
  const mdComponents = headingComponents();
  const chunks = splitArticleForBanners(body);
  const productKey = blogProductKey(categorySlug, post.tags ?? []);

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-dashed border-amber-400 bg-amber-50 text-amber-900">
        <div className="container-editorial flex flex-wrap items-center justify-between gap-3 py-2 text-xs">
          <span>
            <strong>Draft preview</strong> · status: {post.status} · not indexed, admin-only view
          </span>
          <Link
            to="/admin/posts/$id"
            params={{ id }}
            className="rounded-md border border-amber-500 px-2 py-1 hover:bg-amber-100"
          >
            ← Back to editor
          </Link>
        </div>
      </div>

      <ArticleHeader
        category={categoryName}
        title={post.title}
        excerpt={post.excerpt}
        author={author}
        published={formatDate(published)}
        updated={post.updated_at ? formatDate(post.updated_at) : null}
        readMins={post.read_mins}
        path={path}
      />

      <article className="container-editorial w-full max-w-full overflow-hidden px-5 py-12 sm:px-7 md:px-10">
        {cover && (
          <img
            src={cover}
            alt={post.cover_alt ?? post.title}
            title={post.title}
            className="w-full rounded-xl border border-border"
            width={1200}
            height={630}
          />
        )}

        <div className="mt-8 grid min-w-0 gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(14rem,18rem)]">
          <div className="min-w-0">
            <ArticleTakeaways answer={quickAnswer} points={takeaways} />
            <div className="mt-8 lg:hidden">
              <ArticleOutline items={outline} />
            </div>
            <div className="mt-8">
              <Prose>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {chunks[0]}
                </ReactMarkdown>
              </Prose>
            </div>

            <QuickQuoteWidget sourcePage="blog-mid" compact />

            {chunks[1] ? (
              <Prose>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {chunks[1]}
                </ReactMarkdown>
              </Prose>
            ) : null}

            <AskSamCard productKey={productKey} topic={categoryName} />

            {chunks[2] ? (
              <Prose>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {chunks[2]}
                </ReactMarkdown>
              </Prose>
            ) : null}
            <QuickDscrCheck />
            <ProductReportCtaBanner productKey={productKey} variant="article" />
            <ArticleEnding
              path={path}
              title={post.title}
              updatedAt={post.updated_at}
              expertInsight={expertInsight}
              showPreferredSource={false}
            />
          </div>

          <aside className="min-w-0 space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="hidden lg:block">
              <ArticleOutline items={outline} />
            </div>
          </aside>
        </div>
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
      </article>
    </div>
  );
}
